import { ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useRegulatoryFrameworks,
  type FrameworkRow,
  type FrameworkCoverageStatus,
  type RegulatoryFilters,
} from '@/hooks/useRegulatoryQueries'

/**
 * P3 — Framework Coverage card grid.
 *
 * Renders the /regulatory/frameworks response as a responsive 1/2/3-col grid
 * of "framework cards". Each card exposes a native detail-page link for the
 * framework brief while preserving a separate external Reference link.
 *
 * Designed against the real `FrameworkRow` shape from useRegulatoryQueries —
 * not a plan sample. See `CLAUDE.md` pre-dispatch data-shape convention.
 */

const STATUS_LABEL: Record<FrameworkCoverageStatus, string> = {
  full: 'Full coverage',
  partial: 'Partial coverage',
  emerging: 'Emerging',
  'pending-ingest': 'Pending ingest',
}

const STATUS_CLASS: Record<FrameworkCoverageStatus, string> = {
  full: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  partial:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  emerging: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  'pending-ingest': 'bg-muted text-muted-foreground',
}

/**
 * Honest fallback for a `coverageStatus='full'` framework whose detail page
 * cannot yet back the claim (no enabled, published brief). KAN-274 F5: the
 * seed-driven `full` status would otherwise render a strong "Full coverage"
 * badge linking to a detail page with no populated brief. Amber + an
 * in-progress label signals "being built" — lesser than the emerald
 * Full-coverage badge, and distinct from the muted pending-ingest chip.
 */
const COVERAGE_IN_PROGRESS_LABEL = 'Coverage in progress'
const COVERAGE_IN_PROGRESS_CLASS =
  'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'

/**
 * Reconcile the coverage badge with real detail availability. The strong
 * "Full coverage" treatment is only honest when `detailAvailable` is true (a
 * real, enabled, published brief exists). When `coverageStatus='full'` but no
 * detail page backs it, downgrade to an in-progress state rather than
 * over-claiming. All other statuses are unaffected — they never imply a
 * populated full detail page.
 */
export function resolveCoverageBadge(
  fw: Pick<FrameworkRow, 'coverageStatus' | 'detailAvailable'>,
): { label: string; className: string } {
  if (fw.coverageStatus === 'full' && !fw.detailAvailable) {
    return {
      label: COVERAGE_IN_PROGRESS_LABEL,
      className: COVERAGE_IN_PROGRESS_CLASS,
    }
  }
  return {
    label: STATUS_LABEL[fw.coverageStatus],
    className: STATUS_CLASS[fw.coverageStatus],
  }
}

/** Pluralise a unit by count: `pluralise(1, 'month')` → "1 month". */
function pluralise(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'}`
}

export function formatRelative(iso: string | null): string {
  if (!iso) return 'No enforcement tracked yet'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'No enforcement tracked yet'
  const diffDays = Math.floor(
    (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24),
  )
  if (diffDays <= 0) return 'Last enforcement: today'
  if (diffDays < 30)
    return `Last enforcement: ${pluralise(diffDays, 'day')} ago`
  if (diffDays < 365)
    return `Last enforcement: ${pluralise(Math.floor(diffDays / 30), 'month')} ago`
  return `Last enforcement: ${pluralise(Math.floor(diffDays / 365), 'year')} ago`
}

function FrameworkCard({ fw }: { fw: FrameworkRow }) {
  const target = `/regulatory/frameworks/${encodeURIComponent(fw.slug)}`
  const badge = resolveCoverageBadge(fw)
  return (
    <article
      data-testid={`framework-card-${fw.slug}`}
      className="relative rounded-lg border bg-card p-4 hover:border-primary"
    >
      <Link
        to={target}
        aria-label={`View ${fw.name} framework brief`}
        className="absolute inset-0 z-10 rounded-lg cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <header className="flex items-start justify-between gap-2">
        <h3 className="font-semibold">{fw.name}</h3>
      </header>
      <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
        {fw.shortDescription}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Badge variant="outline">{fw.jurisdiction}</Badge>
        <span
          data-testid={`status-chip-${fw.slug}`}
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${badge.className}`}
        >
          {badge.label}
        </span>
      </div>
      <div className="mt-3 text-xs text-muted-foreground">
        {formatRelative(fw.lastEnforcementAt)}
      </div>
      <a
        href={fw.referenceUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open ${fw.name} reference`}
        className="relative z-20 mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ExternalLink className="h-3 w-3" />
        Reference
      </a>
    </article>
  )
}

interface FrameworkCoverageGridProps {
  /**
   * Page-level filter state. P4f (audit item 22): the `/frameworks`
   * endpoint now honours `jurisdiction` (filters the row list) and
   * `timeframe` (window-clamps `lastEnforcementAt`). When omitted, server
   * defaults apply (`90d`, `all`) — preserves backwards compatibility.
   */
  filters?: Pick<RegulatoryFilters, 'jurisdiction' | 'timeframe'>
}

export function FrameworkCoverageGrid({ filters }: FrameworkCoverageGridProps = {}) {
  const { data, isLoading, isError } = useRegulatoryFrameworks(filters)

  if (isLoading) {
    return (
      <section data-testid="p3-framework-grid">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-36 w-full" />
          ))}
        </div>
      </section>
    )
  }
  if (isError || !data) {
    return (
      <section data-testid="p3-framework-grid-error" role="alert">
        <p className="text-sm text-destructive">
          Unable to load framework coverage.
        </p>
      </section>
    )
  }
  if (data.frameworks.length === 0) {
    return (
      <section data-testid="p3-framework-grid-empty">
        <p className="text-sm text-muted-foreground">
          No frameworks configured.
        </p>
      </section>
    )
  }

  return (
    <section data-testid="p3-framework-grid">
      <header className="mb-3">
        <h2 className="text-lg font-semibold">Framework coverage</h2>
      </header>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {data.frameworks.map((fw) => (
          <FrameworkCard key={fw.id} fw={fw} />
        ))}
      </div>
    </section>
  )
}
